import { UserOperation } from '@utility/constants/common';
import type { ITableColumn } from 'types/common/table';
import * as yup from 'yup';

export const DiscountRules = {
  Percentage: 'percentage',
  Fixed: 'fixed',
} as const;

export const DiscountRulesList = [
  {
    text: '%',
    value: DiscountRules.Percentage,
  },
  {
    text: '$',
    value: DiscountRules.Fixed,
  },
];

export const createPOItemColumns: ITableColumn[] = [
  {
    text: '',
    width: 2,
    key: 'image',
  },
  {
    text: 'Product Information',
    width: 4,
    key: 'info',
  },
  {
    text: 'Carton Configuration',
    key: 'ctn-properties',
    width: 3,
  },
  {
    text: 'Carton Quantity',
    key: 'quantity-ctn',
    component: (
      <div className="sk:flex! sk:flex-col!">
        <span>Carton</span>
        <span className="sk:mt-1!">Quantity</span>
      </div>
    ),
    width: 1,
  },
  {
    text: 'Order Prediction (Units)',
    component: (
      <div className="sk:flex! sk:flex-col!">
        <span>Order Prediction</span>
        <span className="sk:mt-1!">(Units)</span>
      </div>
    ),
    key: 'predictedQty',
    width: 2,
  },
  {
    text: 'Quantity (Units)',
    component: (
      <div className="sk:flex! sk:flex-col!">
        <span>Quantity</span>
        <span className="sk:mt-1!">(Units)</span>
      </div>
    ),
    key: 'quantity',
    width: 1,
  },
  {
    text: 'Cost Per Item',
    key: 'Price-Per-Item',
    width: 2,
  },
  {
    text: 'Total',
    key: 'amount',
    width: 2,
  },
];

export const itemColumns = [
  {
    text: '',
    width: 1,
    key: 'image',
  },
  {
    text: 'Product Information',
    width: 3,
    key: 'info',
    sortKey: 'p_sku',
  },
  {
    text: 'Carton Configuration',
    key: 'ctn-properties',
    width: 3,
  },
  {
    text: 'Carton Quantity',
    component: (
      <div className="sk:flex! sk:flex-col!">
        <span>Carton</span>
        <span className="sk:mt-1!">Quantity</span>
      </div>
    ),
    key: 'quantity-ctn',
    width: 1,
    sortKey: 'cartons-quantity',
  },
  {
    text: 'Quantity (Units)',
    component: (
      <div className="sk:flex! sk:flex-col!">
        <span>Quantity</span>
        <span className="sk:mt-1!">(Units)</span>
      </div>
    ),
    key: 'quantity',
    width: 1,
    sortKey: 'qty',
  },
  {
    text: 'Cost Per Item',
    key: 'Price-Per-Item',
    width: 2,
  },
  {
    text: 'Total',
    key: 'amount',
    width: 2,
    sortKey: 'total',
  },
];

export const shipmentItemColumn = [
  {
    text: 'Destination',
    key: 'destination',
    width: 7,
    textAlign: 'start',
  },
  {
    text: 'Status',
    key: 'stats',
    width: 4,
    textAlign: 'start',
    className: 'status-column',
  },
  {
    text: 'Type',
    key: 'shipping_type',
    width: 2,
    textAlign: 'start',
    className: 'type-column',
  },
  {
    text: '',
    key: 'plan_shipment',
    width: 3,
    textAlign: 'start',
  },
];

interface ISchemaData {
  mode: (typeof UserOperation)[keyof typeof UserOperation];
  emailEdit: boolean;
}

export const poSchema = yup.object().shape({
  supplier_id: yup
    .string()
    .when('$schemeData', (schemeData: ISchemaData, schema) =>
      schemeData.mode === UserOperation.create
        ? schema.required()
        : schema.optional(),
    ),
  display_id: yup
    .string()
    .when('$schemeData', (schemeData: ISchemaData, schema) =>
      schemeData.mode === UserOperation.create
        ? schema.required()
        : schema.optional(),
    ),
  memo: yup.string(),
  date_issued: yup
    .string()
    .when('$schemeData', (schemeData: ISchemaData, schema) =>
      schemeData.mode === UserOperation.create
        ? schema.required()
        : schema.optional(),
    ),
  manufacturer_reference_number: yup.string(),
  currency: yup.string().nullable(),
  notify: yup.boolean().default(true),
  email_addresses: yup.array().of(yup.string().required()).min(1),
  price_term: yup.string().required(),
  lead_time: yup.number().required().default(30).integer(),
  down_payment: yup.number().min(0).required().max(100).default(0),
  upon_shipment: yup
    .number()
    .min(0)
    .required()
    .max(100)
    .when(['down_payment'], (down_payment, schema) =>
      down_payment
        ? schema.test(
            'upon_shipment',
            'Invalid Number',
            upon_shipment => upon_shipment + down_payment === 100,
          )
        : schema.test(
            'upon_shipment',
            'Invalid Number',
            upon_shipment => upon_shipment === 100,
          ),
    ),
  discount: yup.string().default('').nullable(true),
  discount_rule: yup.string().default(DiscountRules.Percentage).nullable(true),
});

export const editPOSchema = yup.object().shape({
  manufacturer_reference_number: yup.string(),
  currency: yup.string().nullable(),
  display_id: yup.string().required(),
  notify: yup.boolean().default(true),
  email_addresses: yup.array().of(yup.string().required()).min(1),
});
